import React from 'react';

import type { ITableColumn, IRowActions } from './types';
import { resolveColumnKey } from './resolveColumnKey';
import { resolveColumnTitle } from './resolveColumnTitle';
import ColumnHeaderMenu from '../components/ColumnHeaderMenu';
import ConfigureColumnsButton from '../components/ConfigureColumnsButton';
import RowActionsMenu from '../components/RowActionsMenu';
import type { IColumnWidthState } from '../hooks/useColumnWidths';
import type { IColumnVisibilityState } from '../hooks/useColumnVisibility';

interface IApplyMenuOptions<T extends object> {
  columns: ITableColumn<T>[];
  widthState: IColumnWidthState;
  handleMenuSort: (columnKey: string, order: 'ascend' | 'descend') => void;
  moveColumnLeft: (columnKey: string) => void;
  moveColumnRight: (columnKey: string) => void;
  moveColumnFirst: (columnKey: string) => void;
  moveColumnLast: (columnKey: string) => void;
  setVisibility: (columnKey: string, visible: boolean) => void;
  startResize: (columnKey: string) => void;
  setLiveWidth: (columnKey: string, width: number) => void;
  saveWidth: (columnKey: string, width: number) => void;
  cancelResize: () => void;
}

export const applyWidths = <T extends object>(
  columns: ITableColumn<T>[],
  widthState: IColumnWidthState
): ITableColumn<T>[] => {
  return columns.map(col => {
    const key = resolveColumnKey(col);
    return key
      ? {
          ...col,
          width: widthState[key] ?? col.width,
        }
      : col;
  });
};

export const applyMenu = <T extends object>({
  columns,
  widthState,
  handleMenuSort,
  moveColumnLeft,
  moveColumnRight,
  moveColumnFirst,
  moveColumnLast,
  setVisibility,
  startResize,
  setLiveWidth,
  saveWidth,
  cancelResize,
}: IApplyMenuOptions<T>): ITableColumn<T>[] => {
  return columns.map(col => {
    const key = resolveColumnKey(col);
    if (!key) return col;

    return {
      ...col,
      sorter: false,
      showSorterTooltip: false,
      onHeaderCell: () => ({
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          e.preventDefault();
        },
      }),
      title: (
        <ColumnHeaderMenu
          title={resolveColumnTitle(col.title)}
          columnWidth={widthState[key] && col.width ? Number(col.width) : 0}
          menuOptions={col.columnMenuOptions}
          onSortAsc={() => handleMenuSort(key, 'ascend')}
          onSortDesc={() => handleMenuSort(key, 'descend')}
          onMoveLeft={() => moveColumnLeft(key)}
          onMoveRight={() => moveColumnRight(key)}
          onMoveFirst={() => moveColumnFirst(key)}
          onMoveLast={() => moveColumnLast(key)}
          onHide={() => setVisibility(key, false)}
          onStartResize={() => startResize(key)}
          onLiveWidthChange={w => setLiveWidth(key, w)}
          onResize={w => saveWidth(key, w)}
          onCancelResize={cancelResize}
        />
      ),
    };
  });
};

export const applyRowActions = <T extends object>(
  columns: ITableColumn<T>[],
  rowActions?: IRowActions<T>,
  rowActionsCallback?: (record: T) => IRowActions<T> | undefined
): ITableColumn<T>[] => {
  if (rowActionsCallback) {
    return [
      ...columns,
      {
        key: '__row_actions__',
        width: 48,
        onHeaderCell: () => ({
          className: 'table-header-cell--borderless',
        }),
        align: 'right' as const,
        render: (_: unknown, record: T) => {
          const actions = rowActionsCallback(record);
          if (!actions) return null;
          return <RowActionsMenu record={record} actions={actions} />;
        },
      },
    ];
  }

  if (rowActions) {
    return [
      ...columns,
      {
        key: '__row_actions__',
        width: 48,
        fixed: rowActions.fixed,
        align: rowActions.fixed ? undefined : ('right' as const),
        onHeaderCell: () => ({
          className: 'table-header-cell--borderless',
        }),
        render: (_: unknown, record: T) => (
          <RowActionsMenu record={record} actions={rowActions} />
        ),
      },
    ];
  }

  return columns;
};

interface IApplyConfigColumnOptions<T extends object> {
  columns: ITableColumn<T>[];
  originalColumns: ITableColumn<T>[];
  visibilityState: IColumnVisibilityState;
  setVisibility: (columnKey: string, visible: boolean) => void;
}

export const applyConfigColumn = <T extends object>({
  columns,
  originalColumns,
  visibilityState,
  setVisibility,
}: IApplyConfigColumnOptions<T>): ITableColumn<T>[] => {
  const configButtonTitle = (
    <ConfigureColumnsButton
      columns={originalColumns}
      visibilityState={visibilityState}
      onVisibilityChange={setVisibility}
    />
  );

  const lastColumn = columns[columns.length - 1];
  const hasRowActionsColumn = lastColumn?.key === '__row_actions__';

  if (hasRowActionsColumn) {
    return columns.map((col, index) => {
      if (index === columns.length - 1 && col.key === '__row_actions__') {
        return {
          ...col,
          title: configButtonTitle,
        };
      }
      return col;
    });
  }

  return [
    ...columns,
    {
      key: '__column_config__',
      width: 48,
      fixed: 'right' as const,
      onHeaderCell: () => ({
        className: 'table-header-cell--borderless',
      }),
      title: configButtonTitle,
      render: () => null,
    },
  ];
};
